import { useState } from 'react';
import { useCart } from '../contexts/CartContext';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  boat: {
    name: string;
    price: number;
    image: string;
    passengers: number;
  };
}

export default function BookingModal({ isOpen, onClose, boat }: BookingModalProps) {
  const { addToCart } = useCart();
  const [formData, setFormData] = useState({
    date: '',
    hours: '5'
  });
  const [showSuccess, setShowSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const totalPrice = boat.price * (parseInt(formData.hours) / 5);
    
    addToCart({
      id: `${boat.name}-${Date.now()}`,
      name: boat.name,
      price: totalPrice,
      image: boat.image,
      date: formData.date,
      hours: parseInt(formData.hours),
      passengers: boat.passengers
    });

    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
      setFormData({ date: '', hours: '5' });
    }, 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculatePrice = () => {
    return boat.price * (parseInt(formData.hours) / 5);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md m-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Reservar Embarcación</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <img
              src={boat.image}
              alt={boat.name}
              className="w-20 h-20 object-cover rounded-lg"
            />
            <div>
              <h3 className="font-bold text-lg">{boat.name}</h3>
              <p className="text-teal-100 text-sm">Hasta {boat.passengers} pasajeros</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
              Fecha de Reservación *
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            />
          </div>

          <div>
            <label htmlFor="hours" className="block text-sm font-semibold text-gray-700 mb-2">
              Duración *
            </label>
            <select
              id="hours"
              name="hours"
              value={formData.hours}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="2">2 horas</option>
              <option value="3">3 horas</option>
              <option value="4">4 horas</option>
              <option value="5">5 horas (paquete base)</option>
              <option value="6">6 horas</option>
              <option value="8">8 horas (día completo)</option>
            </select>
          </div>

          {/* Price Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Precio base (5 hrs):</span>
              <span className="font-semibold">${boat.price.toLocaleString('es-MX')}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Duración seleccionada:</span>
              <span className="font-semibold">{formData.hours} horas</span>
            </div>
            <div className="border-t border-gray-200 pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-gray-900">Total:</span>
                <span className="text-2xl font-bold text-teal-600">
                  ${calculatePrice().toLocaleString('es-MX')} MXN
                </span>
              </div>
            </div>
          </div>

          {showSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="ri-check-line text-xl"></i>
              ¡Agregado al carrito exitosamente!
            </div>
          )}

          <button
            type="submit"
            className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Agregar al Carrito
          </button>
        </form>
      </div>
    </div>
  );
}
